import {useEffect, useState, type SyntheticEvent} from 'react'
import {Navigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useAuth} from '../auth/useAuth'
import {trackEvent} from '../../common/analytics/trackEvent'
import InteractivePhone3D from '../components/InteractivePhone3D'
import './LoginPage.css'

type FeatureKey = 'project' | 'shape' | 'bg' | 'light' | 'overlays' | 'scenes' | 'carousel' | 'pay'

const FEATURE_KEYS: FeatureKey[] = ['project', 'shape', 'bg', 'light', 'overlays', 'scenes', 'carousel', 'pay']

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 9.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 3.18 29.93 1 24 1 15.4 1 7.96 5.93 4.34 13.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
)

const FeatureIcon = ({k}: {k: FeatureKey}) => {
  const c = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (k) {
    case 'project':
      return (
        <svg {...c}>
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
      )
    case 'shape':
      return (
        <svg {...c}>
          <rect x="4" y="4" width="7" height="7" rx="1.5" />
          <circle cx="17" cy="7.5" r="3.5" />
          <path d="M7.5 14l-3.5 6h7l-3.5-6zM14 14h6v6h-6z" />
        </svg>
      )
    case 'bg':
      return (
        <svg {...c}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M3 16l5-5 4 4 3-3 6 6" />
          <circle cx="9" cy="9" r="1.5" />
        </svg>
      )
    case 'light':
      return (
        <svg {...c}>
          <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.6 1 2.5h6c0-.9.3-1.8 1-2.5A6 6 0 0 0 12 3z" />
        </svg>
      )
    case 'overlays':
      return (
        <svg {...c}>
          {/* T glyph on the left — represents text */}
          <path d="M3 5h7M6.5 5v14" />
          {/* image frame on the right — represents a sprite */}
          <rect x="12" y="7" width="9" height="11" rx="1.5" />
          <circle cx="14.5" cy="10.5" r="0.9" />
          <path d="M12.6 16.5l2.4-2.4 2 2 2.4-2.4" />
        </svg>
      )
    case 'scenes':
      return (
        <svg {...c}>
          <rect x="3" y="6" width="13" height="13" rx="2" />
          <path d="M8 3h11a2 2 0 0 1 2 2v11" />
        </svg>
      )
    case 'carousel':
      return (
        <svg {...c}>
          <rect x="2" y="5" width="6" height="14" rx="1.2" />
          <rect x="9" y="5" width="6" height="14" rx="1.2" />
          <rect x="16" y="5" width="6" height="14" rx="1.2" />
        </svg>
      )
    case 'pay':
      return (
        <svg {...c}>
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9h-4a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4H9M12 7v2M12 15v2" />
        </svg>
      )
    default:
      return null
  }
}

const MailIcon = ({size = 16}: {size?: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 7 9-7" />
  </svg>
)

const EnvelopeOpenIcon = ({size = 28}: {size?: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-6 9 6v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" />
    <path d="M3 9l9 7 9-7" />
  </svg>
)

const deriveEntryPoint = (): string => {
  if (typeof document === 'undefined') return 'direct'
  const ref = document.referrer
  if (!ref) return 'direct'
  try {
    const url = new URL(ref)
    if (url.host !== window.location.host) return 'external'
    const path = url.pathname
    if (path === '/' || path === '') return 'landing'
    if (path.startsWith('/pricing')) return 'pricing'
    if (path.startsWith('/free') || path.startsWith('/icon-generator')) return 'free_tool'
    return 'internal_other'
  } catch {
    return 'direct'
  }
}

const LoginPage = () => {
  const {user, loading, signInWithGoogle, sendMagicLink} = useAuth()
  const {t} = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)

  useEffect(() => {
    trackEvent('login_page_viewed', {entry_point: deriveEntryPoint()})
  }, [])

  if (loading) return <p style={{padding: 32, textAlign: 'center'}}>{t('a11y.loading')}</p>
  if (user) return <Navigate to="/projects" replace />

  const handleGoogleSignIn = async () => {
    setError(null)
    setBusy(true)
    trackEvent('google_signin_clicked')
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error'))
    } finally {
      setBusy(false)
    }
  }

  const handleSendMagicLink = async (e: SyntheticEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    trackEvent('magic_link_requested')
    try {
      await sendMagicLink(email)
      setSentTo(email)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error'))
    } finally {
      setBusy(false)
    }
  }

  const handleResetSent = () => {
    trackEvent('magic_link_resent')
    setSentTo(null)
    setEmail('')
    setError(null)
  }

  const renderForm = () => {
    if (sentTo) {
      return (
        <div className="auth-sent">
          <div className="auth-sent-icon">
            <EnvelopeOpenIcon />
          </div>
          <h1>{t('auth.magicLinkSentTitle')}</h1>
          <p>
            {t('auth.magicLinkSentBodyPrefix')}
            <b>{sentTo}</b>
            {t('auth.magicLinkSentBodySuffix')}
          </p>
          <div className="auth-sent-tips">
            {t('auth.magicLinkSentTipPrefix')}
            <button type="button" onClick={handleResetSent}>
              {t('auth.magicLinkSentResend')}
            </button>
            {t('auth.magicLinkSentTipSuffix')}
          </div>
        </div>
      )
    }

    return (
      <form className="auth-form" onSubmit={handleSendMagicLink}>
        <div className="auth-form-head">
          <h1>{t('auth.formHeadTitle')}</h1>
          <p>{t('auth.formHeadSub')}</p>
        </div>

        {error && <p className="auth-error">{error}</p>}

        <button type="button" className="auth-google" onClick={handleGoogleSignIn} disabled={busy}>
          <GoogleMark />
          {t('auth.continueWithGoogle')}
        </button>

        <div className="auth-divider">{t('auth.divider')}</div>

        <div className="auth-fields">
          <div className="auth-field">
            <label className="auth-label" htmlFor="auth-email">
              {t('auth.email')}
            </label>
            <input
              id="auth-email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              disabled={busy}
              required
            />
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={busy || !email}>
          <MailIcon />
          {busy ? t('auth.sending') : t('auth.sendMagicLink')}
        </button>
      </form>
    )
  }

  return (
    <div className="auth-root">
      <div className="auth-shell">
        <div className="auth-visual-bg" aria-hidden="true" />

        <div className="auth-headline">
          <div className="auth-eyebrow">{t('auth.featuresEyebrow')}</div>
          <h2 className="auth-head-title">
            {t('auth.featuresHeadPrefix')}
            <em>{t('auth.featuresHeadEm')}</em>
            {t('auth.featuresHeadSuffix')}
          </h2>
        </div>

        <div className="auth-stage-wrap">
          <InteractivePhone3D />
        </div>

        <div className="auth-features-list-wrap">
          <ul className="auth-features-list">
            {FEATURE_KEYS.map((k) => (
              <li key={k} className="auth-feature">
                <span className="auth-feature-icon">
                  <FeatureIcon k={k} />
                </span>
                <div>
                  <div className="auth-feature-label">{t(`auth.feats.${k}.label`)}</div>
                  <div className="auth-feature-desc">{t(`auth.feats.${k}.desc`)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="auth-form-panel">{renderForm()}</div>
      </div>
    </div>
  )
}

export default LoginPage
